import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { logError, LogCategory } from "utils/sentryLogger";
import { request } from "../useOkoApi/useOkoApi";

/**
 * Standards data is fetched from `standards/all_standards`, optionally with
 * per-standard challenge counts (`?include_counts=true`). The counts variant is
 * the slowest call in the app, so consumers can gate it behind `enabled` and
 * only pay for it when the data is actually rendered.
 *
 * React Query gives us the app-wide dedup + cache that the old hand-rolled
 * StandardsProvider provided: a shared `queryKey` means every consumer of the
 * same (includeCounts) variant reads one cached result, and `usePreloadStandards`
 * can warm that same cache in the background.
 */

// Stable empty reference so gated/loading consumers don't re-render on identity change.
const EMPTY_STANDARDS = [];

const standardsQueryOptions = (includeCounts) => ({
  queryKey: ["standards", { includeCounts: !!includeCounts }],
  queryFn: () =>
    request(
      includeCounts ? "standards/all_standards?include_counts=true" : "standards/all_standards",
      { method: "GET" },
    ),
  // Standards rarely change within a session; match the previous
  // fetch-once-and-keep-forever behavior so navigations never refetch.
  // (React Query v4 uses `cacheTime`; v5 renamed it to `gcTime`.)
  staleTime: Infinity,
  cacheTime: Infinity,
});

/**
 * Provider kept as a thin passthrough for backward compatibility. Caching now
 * lives in the shared QueryClient mounted at the app root, so this no longer
 * holds any state — it simply preserves the existing App tree / import.
 */
export const StandardsProvider = ({ children }) => <>{children}</>;

/**
 * Access standards data, fetching (and caching) it on first use.
 * @param {boolean} includeCounts - Whether to fetch standards with challenge counts.
 * @param {boolean} enabled - When false, no request is issued (returns []). Defaults
 *   to true so existing single-arg callers are unaffected.
 * @returns {Array} Standards data array.
 */
export const useStandards = (includeCounts = false, enabled = true) => {
  const { data } = useQuery({
    ...standardsQueryOptions(includeCounts),
    enabled,
    onError: (err) => {
      console.error(
        includeCounts ? "Error fetching standards with counts:" : "Error fetching standards:",
        err,
      );
      logError(
        includeCounts ? "Failed to fetch standards with counts" : "Failed to fetch standards",
        err,
        { category: LogCategory.ERROR },
      );
    },
  });

  return data ?? EMPTY_STANDARDS;
};

/**
 * Access the full standards query result (data + loading/error states) for the
 * given variant. Retained for backward compatibility with the previous context hook.
 * @param {boolean} includeCounts - Whether to read the with-counts variant.
 */
export const useStandardsContext = (includeCounts = false) =>
  useQuery({ ...standardsQueryOptions(includeCounts), enabled: false });

/**
 * Preload standards data into the shared cache in the background without
 * blocking the UI. Useful for warming the cache before the data is displayed
 * (e.g. after first paint) so a later consumer renders instantly.
 * @param {boolean} includeCounts - Whether to preload the with-counts variant.
 * @param {boolean} enabled - When false, no prefetch is issued. Defaults to true
 *   so existing single-arg callers are unaffected; callers can pass an idle flag
 *   to defer warming until after first paint.
 */
export const usePreloadStandards = (includeCounts = false, enabled = true) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    // Silently prefetch — refetches are a no-op if the cache is already warm.
    queryClient.prefetchQuery(standardsQueryOptions(includeCounts)).catch(() => {
      // Silently fail - this is just a preload.
    });
  }, [queryClient, includeCounts, enabled]);
};
